import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface CommuteDestination {
  name: string;
  address: string;
  importance: 'high' | 'medium' | 'low';
}

export interface UserPreferences {
  // Budget
  budgetMin: number;
  budgetMax: number;

  // Commute destinations
  commutes: CommuteDestination[];

  // Scoring weights (must sum to 100)
  weights: {
    price: number;         // Price vs budget
    commute: number;       // Commute time
    schools: number;       // School ratings
    safety: number;        // Neighborhood safety
    walkability: number;   // Walk/Transit/Bike scores
    lotSize: number;       // Lot size / sqft ratio
    condition: number;     // Age and condition
    dom: number;           // Days on market (negotiation)
    value: number;         // Price per sqft vs comps
  };

  // Preferences
  minBeds: number;
  minBaths: number;
  minSqft: number;
  maxYearBuilt: number;
  maxCommute: number;       // Max acceptable commute (minutes)
  minSchoolRating: number;  // 1-10 scale
}

const DEFAULT_PREFERENCES: UserPreferences = {
  budgetMin: 200000,
  budgetMax: 400000,
  commutes: [
    { name: 'Office', address: '', importance: 'high' },
    { name: 'Gym', address: '', importance: 'medium' },
  ],
  weights: {
    price: 20,
    commute: 15,
    schools: 10,
    safety: 15,
    walkability: 10,
    lotSize: 10,
    condition: 10,
    dom: 5,
    value: 5,
  },
  minBeds: 3,
  minBaths: 2,
  minSqft: 1500,
  maxYearBuilt: 1960, // Nothing older than
  maxCommute: 30,
  minSchoolRating: 5,
};

interface PreferencesContextValue {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  updateWeight: (key: keyof UserPreferences['weights'], value: number) => void;
  addCommute: (destination: CommuteDestination) => void;
  removeCommute: (index: number) => void;
  updateCommute: (index: number, destination: Partial<CommuteDestination>) => void;
  resetToDefaults: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
      } catch {
        return DEFAULT_PREFERENCES;
      }
    }
    return DEFAULT_PREFERENCES;
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setPreferences((prev) => ({ ...prev, ...updates }));
  };

  const updateWeight = (key: keyof UserPreferences['weights'], value: number) => {
    setPreferences((prev) => ({
      ...prev,
      weights: { ...prev.weights, [key]: value },
    }));
  };

  const addCommute = (destination: CommuteDestination) => {
    setPreferences((prev) => ({
      ...prev,
      commutes: [...prev.commutes, destination],
    }));
  };

  const removeCommute = (index: number) => {
    setPreferences((prev) => ({
      ...prev,
      commutes: prev.commutes.filter((_, i) => i !== index),
    }));
  };

  const updateCommute = (index: number, updates: Partial<CommuteDestination>) => {
    setPreferences((prev) => ({
      ...prev,
      commutes: prev.commutes.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    }));
  };

  const resetToDefaults = () => {
    setPreferences(DEFAULT_PREFERENCES);
  };

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        updatePreferences,
        updateWeight,
        addCommute,
        removeCommute,
        updateCommute,
        resetToDefaults,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}

// Calculate personal score for a property based on preferences
export function calculatePersonalScore(
  property: {
    price: number;
    beds?: number;
    baths?: number;
    sqft?: number;
    yearBuilt?: number;
    dom?: number;
    lotSize?: number;
    areaScore?: number;
    qualityScore?: number;
    // External data (if available)
    walkScore?: number;
    transitScore?: number;
    schoolRating?: number;
    safetyScore?: number;
    commuteMinutes?: number;
    pricePerSqft?: number;
    medianPricePerSqft?: number;
  },
  preferences: UserPreferences
): { score: number; breakdown: Record<string, number>; matches: string[]; issues: string[] } {
  const breakdown: Record<string, number> = {};
  const matches: string[] = [];
  const issues: string[] = [];
  const { weights } = preferences;

  // 1. Price vs Budget (0-100)
  let priceScore = 0;
  if (property.price <= preferences.budgetMin) {
    priceScore = 100;
    matches.push('Well under budget');
  } else if (property.price <= preferences.budgetMax) {
    const range = preferences.budgetMax - preferences.budgetMin;
    const position = property.price - preferences.budgetMin;
    priceScore = 100 - (position / range) * 50;
    matches.push('Within budget');
  } else {
    const overBudget = ((property.price - preferences.budgetMax) / preferences.budgetMax) * 100;
    priceScore = Math.max(0, 50 - overBudget);
    issues.push(`$${Math.round((property.price - preferences.budgetMax) / 1000)}K over budget`);
  }
  breakdown.price = priceScore;

  // 2. Commute (use provided or estimate from area score)
  let commuteScore = 50; // Default
  if (property.commuteMinutes !== undefined) {
    if (property.commuteMinutes <= preferences.maxCommute) {
      commuteScore = 100 - (property.commuteMinutes / preferences.maxCommute) * 30;
      matches.push(`${property.commuteMinutes} min commute`);
    } else {
      commuteScore = Math.max(0, 70 - ((property.commuteMinutes - preferences.maxCommute) / preferences.maxCommute) * 50);
      issues.push(`${property.commuteMinutes} min commute (target: ${preferences.maxCommute})`);
    }
  }
  breakdown.commute = commuteScore;

  // 3. Schools (use provided or default)
  let schoolScore = property.schoolRating ? property.schoolRating * 10 : 50;
  if (property.schoolRating !== undefined) {
    if (property.schoolRating >= preferences.minSchoolRating) {
      matches.push(`${property.schoolRating}/10 schools`);
    } else {
      issues.push(`Schools rated ${property.schoolRating}/10`);
    }
  }
  breakdown.schools = schoolScore;

  // 4. Safety (use areaScore as proxy if not provided)
  const safetyScore = property.safetyScore ?? property.areaScore ?? 50;
  breakdown.safety = safetyScore;
  if (safetyScore >= 70) {
    matches.push('Safe neighborhood');
  } else if (safetyScore < 50) {
    issues.push('Lower safety score');
  }

  // 5. Walkability
  let walkScore = 50;
  if (property.walkScore !== undefined || property.transitScore !== undefined) {
    const walk = property.walkScore ?? 0;
    const transit = property.transitScore ?? 0;
    walkScore = Math.max(walk, (walk + transit) / 2);
    if (walkScore >= 70) {
      matches.push('Very walkable');
    }
  }
  breakdown.walkability = walkScore;

  // 6. Lot size / sqft ratio
  let lotScore = 50;
  if (property.lotSize && property.sqft) {
    const ratio = property.lotSize / property.sqft;
    if (ratio >= 5) {
      lotScore = 90;
      matches.push('Large lot');
    } else if (ratio >= 3) {
      lotScore = 70;
    } else if (ratio >= 2) {
      lotScore = 50;
    } else {
      lotScore = 30;
      issues.push('Small lot');
    }
  }
  breakdown.lotSize = lotScore;

  // 7. Condition / Age
  let conditionScore = property.qualityScore ?? 50;
  if (property.yearBuilt !== undefined) {
    const age = new Date().getFullYear() - property.yearBuilt;
    if (age <= 10) {
      conditionScore = Math.max(conditionScore, 90);
      matches.push('Recently built');
    } else if (age <= 20) {
      conditionScore = Math.max(conditionScore, 75);
    } else if (age <= 40) {
      conditionScore = Math.max(conditionScore, 60);
    } else if (property.yearBuilt < preferences.maxYearBuilt) {
      conditionScore = Math.min(conditionScore, 40);
      issues.push(`Built ${property.yearBuilt} (old)`);
    }
  }
  breakdown.condition = conditionScore;

  // 8. Days on Market (negotiation potential)
  let domScore = 50;
  if (property.dom !== undefined && property.dom !== null && typeof property.dom === 'number') {
    if (property.dom >= 60) {
      domScore = 80;
      matches.push(`${property.dom} DOM - negotiation room`);
    } else if (property.dom >= 30) {
      domScore = 60;
    } else if (property.dom <= 7 && property.dom >= 0) {
      domScore = 30;
      issues.push(`Hot property (${property.dom} DOM)`);
    }
  }
  breakdown.dom = domScore;

  // 9. Value (price per sqft vs comps)
  let valueScore = 50;
  if (property.pricePerSqft && property.medianPricePerSqft) {
    const ratio = property.pricePerSqft / property.medianPricePerSqft;
    if (ratio <= 0.85) {
      valueScore = 90;
      matches.push('Below market value');
    } else if (ratio <= 0.95) {
      valueScore = 70;
    } else if (ratio <= 1.05) {
      valueScore = 50;
    } else if (ratio <= 1.15) {
      valueScore = 30;
    } else {
      valueScore = 15;
      issues.push('Above market value');
    }
  } else if (property.sqft && property.price) {
    // Estimate value score from price per sqft
    const ppsf = property.price / property.sqft;
    if (ppsf < 100) {
      valueScore = 80;
    } else if (ppsf < 150) {
      valueScore = 60;
    } else if (ppsf < 200) {
      valueScore = 40;
    } else {
      valueScore = 20;
    }
  }
  breakdown.value = valueScore;

  // Calculate weighted total
  let totalWeight = 0;
  let weightedSum = 0;

  for (const [key, weight] of Object.entries(weights)) {
    const score = breakdown[key] ?? 50;
    weightedSum += score * weight;
    totalWeight += weight;
  }

  const finalScore = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 50;

  // Check minimum requirements
  if (property.beds !== undefined && property.beds < preferences.minBeds) {
    issues.push(`${property.beds} beds (need ${preferences.minBeds})`);
  }
  if (property.baths !== undefined && property.baths < preferences.minBaths) {
    issues.push(`${property.baths} baths (need ${preferences.minBaths})`);
  }
  if (property.sqft !== undefined && property.sqft < preferences.minSqft) {
    issues.push(`${property.sqft} sqft (need ${preferences.minSqft})`);
  }

  return { score: finalScore, breakdown, matches, issues };
}
